'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { BlocksContent } from '@strapi/blocks-react-renderer';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import GlobalBlockRenderer from '@/components/ContentBlocks/GlobalBlockRenderer/GlobalBlockRenderer';
import { trackEvent } from '@/lib/analytics/gtm';

interface FormContactUsProps {
  content:
    | {
        contactUsFormHeader: BlocksContent;
      }
    | undefined;
}

const contactUsSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().min(1, 'Email is required').email('Valid email is required'),
  mobile: z
    .string()
    .min(1, 'Mobile is required')
    .regex(/^(\+61|0)4\d{2}[-\s]?\d{3}[-\s]?\d{3}$/, 'Valid Australian mobile required'),
  message: z.string().optional(),
  terms: z.literal(true, { message: 'You must accept the terms and conditions' }),
});

type ContactUsFormValues = z.infer<typeof contactUsSchema>;

export function FormContactUs(props: FormContactUsProps) {
  const theme = useTheme();
  const [isSubmitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ContactUsFormValues>({
    resolver: zodResolver(contactUsSchema),
    defaultValues: {
      name: '',
      email: '',
      mobile: '',
      message: '',
      terms: undefined as unknown as true,
    },
  });

  const onSubmit = async (values: ContactUsFormValues) => {
    setSubmitError(false);

    const response = await fetch('/api/enquiry', {
      method: 'POST',
      body: JSON.stringify(values),
    });

    if (response.ok) {
      const content = await response.json();

      if (content.submitted) {
        setSubmitted(true);
        trackEvent({ feature: 'contact-form', action: 'form_submit', isKeyEvent: true });
        return;
      }
    }

    setSubmitError(true);
  };

  return (
    <Box
      data-component="FormContactUs"
      sx={{
        maxWidth: `100%`,
        paddingInline: theme.spacing(4),
        paddingBottom: theme.spacing(2),
      }}
    >
      {props?.content?.contactUsFormHeader ? (
        <GlobalBlockRenderer content={props.content.contactUsFormHeader ?? []} />
      ) : (
        <>
          <Box sx={{ mb: 4 }}>
            <Image src="/assets/logo.svg" alt="Lend Logo" width={102} height={50} />
          </Box>

          <Typography id="modal-modal-title" variant="h1" mb={1}>
            Get <strong>STARTED</strong>
          </Typography>
          <Typography mb={2} variant="small">
            Great News. Great to meet you.
            <br />
            Let's get started by getting a few details.
          </Typography>
        </>
      )}
      <Box display={isSubmitted ? 'block' : 'none'}>
        <Alert severity="success">Thank you for submitting your enquiry.</Alert>
      </Box>
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          maxWidth: `100%`,
          gap: 2,
          input: {
            p: 1,
            lh: 1,
          },
          ' *.MuiInputLabel-shrink': {
            background: theme.palette.purple[300],
            color: `${theme.palette.purple[900]}`,
            py: 0.5,
            px: 1.5,
            marginTop: `calc(${theme.spacing(0.75)} * -1)`,
            borderRadius: 4,
          },
          ' *.MuiInputLabel-shrink.Mui-focused': {
            color: `${theme.palette.brand.secondary} !important`,
          },
        }}
      >
        {submitError && (
          <Alert severity="error">
            An error occurred whilst trying to submit your enquiry. Please try again later.
          </Alert>
        )}
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              variant="outlined"
              label="Name"
              placeholder="Name*"
              error={!!errors.name}
              helperText={errors.name?.message}
              sx={{
                my: 1,
                mt: 3,
                input: {
                  p: 1,
                },
              }}
            />
          )}
        />
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              variant="outlined"
              label="Email"
              placeholder="Email*"
              type="email"
              error={!!errors.email}
              helperText={errors.email?.message}
              sx={{ my: 1 }}
            />
          )}
        />
        <Controller
          name="mobile"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              variant="outlined"
              label="Mobile"
              placeholder="Mobile*"
              type="tel"
              error={!!errors.mobile}
              helperText={errors.mobile?.message}
              sx={{ my: 1 }}
            />
          )}
        />
        <Controller
          name="message"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              variant="outlined"
              label="Message"
              placeholder="Message*"
              multiline
              minRows={2}
              error={!!errors.message}
              helperText={errors.message?.message}
              sx={{
                my: 1,
                textarea: {
                  py: 1,
                  px: 0.95,
                  '&::placeholder': {
                    color: theme.palette.purple[500],
                  },
                },
              }}
            />
          )}
        />
        {errors.terms && <Alert severity="error">{errors.terms.message}</Alert>}
        <Box
          sx={{
            fontSize: theme.typography.h4.fontSize,
            '*': {
              fontSize: theme.typography.h4.fontSize,
            },
          }}
        >
          <Controller
            name="terms"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value === true}
                    sx={{
                      svg: {
                        width: '2.5rem',
                        height: '2.5rem',
                        marginLeft: theme.spacing(2),
                        marginRight: theme.spacing(2),
                        path: {
                          fill: theme.palette.common.white,
                        },
                      },
                    }}
                    onChange={(e) => field.onChange(e.target.checked ? true : undefined)}
                  />
                }
                label={
                  <Box sx={{}}>
                    I agree to{' '}
                    <Link href="https://www.lend.com.au/terms">terms &amp; conditions</Link> and{' '}
                    <Link href="https://www.lend.com.au/privacy">privacy policy</Link>
                  </Box>
                }
              />
            )}
          />
        </Box>
        <Button
          type="submit"
          variant="contained"
          sx={{
            mt: 2,
            mb: 1,
            mx: 'auto',
            width: '20rem',
            minWidth: 'unset',
            display: 'block',
            fontSize: theme.typography.h4.fontSize,
          }}
          disabled={isSubmitting}
        >
          Send Message
        </Button>
      </Box>
    </Box>
  );
}
